import {
  DataEntryOption,
  DetailRequirement,
  EngagementTaskTrait,
  MultipleTaskTraitDetail,
  SingularTaskTraitDetail,
  TaskDataResponse,
} from '@cibo/core'
import { FriendlyError, Markdown } from '@cibo/ui'
import { Checkbox, FormControlLabel, FormGroup, styled } from '@mui/material'
import { useEffect, useState } from 'react'

const StyledFormControlLabel = styled(FormControlLabel)(() => ({
  marginLeft: 0,
}))

type DataEntryCheckboxListProps = {
  options: DataEntryOption[]
  detailRequirements?: DetailRequirement[]
  trait: EngagementTaskTrait
  taskData?: TaskDataResponse
  handleChangeDetail: (
    updates:
      | { userDetails: MultipleTaskTraitDetail[] | SingularTaskTraitDetail[] }
      | { wfDetails: MultipleTaskTraitDetail[] | SingularTaskTraitDetail[] },
    onCatch?: () => void
  ) => void
}

export const DataEntryCheckboxList = ({
  options,
  detailRequirements,
  trait,
  taskData,
  handleChangeDetail,
}: DataEntryCheckboxListProps) => {
  const { userDetails, wfDetails } = taskData || {}

  const [checkboxValues, setCheckboxValues] = useState<string[]>([])

  useEffect(() => {
    if (!checkboxValues.length) {
      if (trait.dataType === 'user') {
        const userDetail = userDetails?.find(userDetail => userDetail.traitId === trait.traitId)
        if (userDetail) {
          setCheckboxValues(userDetail.value as string[])
        }
      } else if (trait.dataType === 'workflow') {
        const wfDetail = wfDetails?.find(wfDetail => wfDetail.traitId === trait.traitId)
        if (wfDetail) {
          setCheckboxValues(wfDetail.value as string[])
        }
      }
    }
  }, [userDetails, wfDetails, trait.traitId])

  if (!taskData) {
    return <FriendlyError message="Task data is not available." />
  }

  return (
    <FormGroup sx={{ pt: 1 }}>
      {options?.map((option: DataEntryOption) => (
        <StyledFormControlLabel
          key={option.value}
          control={
            <Checkbox
              checked={!!checkboxValues?.includes(option.value)}
              onChange={(event, value) => {
                setCheckboxValues(prev => {
                  const previousValue = typeof prev === 'string' ? [prev] : prev
                  const isRemoving = prev.includes(option.value)
                  const values = option.exclusive
                    ? [option.value]
                    : isRemoving
                    ? previousValue.filter(valueItem => valueItem !== option.value)
                    : [...previousValue, option.value].filter(
                        valueItem => !options.find(option => option.value === valueItem)?.exclusive
                      )
                  const updateValue = [
                    detailRequirements?.[0].year
                      ? {
                          traitId: trait.traitId,
                          value: values,
                          year: detailRequirements[0].year,
                        }
                      : { traitId: trait.traitId, value: values },
                  ]
                  trait.dataType === 'user'
                    ? handleChangeDetail({ userDetails: updateValue }, () =>
                        setCheckboxValues(previousValue)
                      )
                    : handleChangeDetail({ wfDetails: updateValue }, () =>
                        setCheckboxValues(previousValue)
                      )

                  return values
                })
              }}
              value={option.value}
            />
          }
          label={<Markdown>{option.label}</Markdown>}
        />
      ))}
    </FormGroup>
  )
}
